"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, AlertCircle, Copy, Check, ChevronDown, ChevronUp, Globe, Share2 } from "lucide-react";
import { useMemo, useState } from "react";
import { ModelCodeExampleDialog } from "@/components/models/model-code-example-dialog";
import { ModelCtaButton } from "@/components/models/model-cta-button";
import { ModelStatusBadge } from "@/components/models/model-status-badge";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card } from "@/lib/components/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/lib/components/dropdown-menu";
import { TooltipProvider } from "@/lib/components/tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/lib/components/tooltip";
import { formatContextSize, formatDeprecationDate } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import { getProviderIcon } from "@llmgateway/shared/components";
import type { ApiModel, ApiModelProviderMapping, ApiProvider } from "@/lib/fetch-models";
import type { StabilityLevel } from "@llmgateway/models";
import type { LucideProps } from "lucide-react";
interface ModelWithProviders extends ApiModel {
  providerDetails: Array<{
    provider: ApiModelProviderMapping;
    providerInfo: ApiProvider;
  }>;
}
function PriceCell(t0) {
  const $ = _c(14);
  const {
    label,
    price,
    discount,
    unit,
    formatPrice,
    multiplier: t1
  } = t0;
  const multiplier = t1 === undefined ? 1 : t1;
  const adjustedPrice = price !== null && price !== undefined && multiplier !== 1 ? String(Number(price) * multiplier) : price;
  let t2;
  if ($[0] !== adjustedPrice || $[1] !== discount || $[2] !== formatPrice) {
    t2 = formatPrice(adjustedPrice, discount);
    $[0] = adjustedPrice;
    $[1] = discount;
    $[2] = formatPrice;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const formatted = t2;
  let t3;
  if ($[4] !== label) {
    t3 = <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">{label}</div>;
    $[4] = label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== formatted) {
    t4 = <div className="font-semibold text-foreground text-sm tabular-nums">{formatted}</div>;
    $[6] = formatted;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== unit) {
    t5 = <div className="text-[10px] text-muted-foreground/50">{unit}</div>;
    $[8] = unit;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = <div className="text-center">{t3}{t4}{t5}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function StabilityDot(t0) {
  const $ = _c(12);
  const {
    stability
  } = t0;
  const {
    t
  } = useI18n();
  const level = stability ?? "stable";
  const colorClass = level === "stable" ? "bg-emerald-500" : level === "beta" ? "bg-amber-500" : "bg-red-500";
  let t1;
  if ($[0] !== colorClass) {
    t1 = cn("inline-block h-2 w-2 rounded-full shrink-0", colorClass);
    $[0] = colorClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <TooltipTrigger asChild={true}><span className={t1} /></TooltipTrigger>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== level || $[5] !== t) {
    t3 = level === "stable" ? t("public.common.stable") : level.charAt(0).toUpperCase() + level.slice(1);
    $[4] = level;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <TooltipContent side="top" className="text-xs">{t3}</TooltipContent>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <Tooltip>{t2}{t4}</Tooltip>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function isImageGenModel(model: Pick<ApiModel, "output">): boolean {
  return Array.isArray(model.output) && model.output.includes("image");
}
function hasEstimatedImageCost(mapping: ApiModelProviderMapping): boolean {
  return Boolean(mapping.imageOutputPrice && mapping.imageOutputTokensByResolution);
}
export function ModelCard(t0) {
  const $ = _c(101);
  const {
    model,
    shouldShowStabilityWarning,
    getCapabilityIcons,
    goToModel,
    formatPrice
  } = t0;
  const {
    t
  } = useI18n();
  const [copiedModel, setCopiedModel] = useState(null);
  const [showAllProviders, setShowAllProviders] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = text => {
      navigator.clipboard.writeText(text);
      setCopiedModel(text);
      setTimeout(() => setCopiedModel(null), 2000);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const copyToClipboard = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Date();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const now = t2;
  let t3;
  if ($[2] !== model.providerDetails) {
    t3 = model.providerDetails.length > 0 && model.providerDetails.every(_temp);
    $[2] = model.providerDetails;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const allHaveDeactivatedAt = t3;
  let t4;
  if ($[4] !== allHaveDeactivatedAt || $[5] !== model.providerDetails) {
    t4 = !allHaveDeactivatedAt && model.providerDetails.length > 0 && model.providerDetails.every(_temp2);
    $[4] = allHaveDeactivatedAt;
    $[5] = model.providerDetails;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const allHaveDeprecatedAt = t4;
  let t5;
  if ($[7] !== allHaveDeactivatedAt || $[8] !== model.providerDetails) {
    t5 = allHaveDeactivatedAt && model.providerDetails.every(t6 => {
      const {
        provider: provider_1
      } = t6;
      return new Date(provider_1.deactivatedAt) <= now;
    });
    $[7] = allHaveDeactivatedAt;
    $[8] = model.providerDetails;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const deactivationAllPast = t5;
  let t6;
  if ($[10] !== allHaveDeprecatedAt || $[11] !== model.providerDetails) {
    t6 = allHaveDeprecatedAt && model.providerDetails.every(t7 => {
      const {
        provider: provider_2
      } = t7;
      return new Date(provider_2.deprecatedAt) <= now;
    });
    $[10] = allHaveDeprecatedAt;
    $[11] = model.providerDetails;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const deprecationAllPast = t6;
  const hasProviderStabilityWarning = _temp3;
  let t7;
  if ($[13] !== model.providerDetails) {
    const map = new Map();
    for (const {
      provider: provider_4,
      providerInfo
    } of model.providerDetails) {
      const key = provider_4.providerId;
      if (!map.has(key)) {
        map.set(key, {
          providerInfo,
          providerId: key,
          mappings: []
        });
      }
      map.get(key).mappings.push(provider_4);
    }
    t7 = Array.from(map.values());
    $[13] = model.providerDetails;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const groupedByProvider = t7;
  let max = 0;
  if ($[15] !== max || $[16] !== model.providerDetails) {
    for (const {
      provider: provider_5
    } of model.providerDetails) {
      if (provider_5.discount) {
        const d = parseFloat(provider_5.discount);
        if (d > max) {
          max = d;
        }
      }
    }
    $[15] = max;
    $[16] = model.providerDetails;
    $[17] = max;
  } else {
    max = $[17];
  }
  const bestDiscount = max;
  let t8;
  bb0: {
    if (model.providerDetails.length === 0) {
      let t9;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = [];
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      t8 = t9;
      break bb0;
    }
    let result;
    if ($[19] !== getCapabilityIcons || $[20] !== model) {
      const seen = new Set();
      result = [];
      for (const {
        provider: provider_6
      } of model.providerDetails) {
        for (const cap of getCapabilityIcons(provider_6, model)) {
          if (!seen.has(cap.label)) {
            seen.add(cap.label);
            result.push(cap);
          }
        }
      }
      $[19] = getCapabilityIcons;
      $[20] = model;
      $[21] = result;
    } else {
      result = $[21];
    }
    t8 = result;
  }
  const capabilities = t8;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = model.name ?? model.id;
  let t11;
  if ($[23] !== t10) {
    t11 = <div className="min-w-0 flex-1"><h3 className="text-lg font-bold text-foreground tracking-tight truncate">{t10}</h3></div>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== model.stability || $[26] !== shouldShowStabilityWarning) {
    t12 = shouldShowStabilityWarning(model.stability) && <AlertTriangle className="h-4 w-4 text-amber-400" />;
    $[25] = model.stability;
    $[26] = shouldShowStabilityWarning;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== model.id) {
    t13 = <div onClick={_temp4} onMouseDown={_temp5}><ModelCodeExampleDialog modelId={model.id} /></div>;
    $[28] = model.id;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t12 || $[31] !== t13) {
    t14 = <div className="flex items-center gap-1.5 shrink-0">{t12}{t13}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t11 || $[34] !== t14) {
    t15 = <div className="flex items-start justify-between gap-3 mb-2">{t11}{t14}</div>;
    $[33] = t11;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== model.family) {
    t16 = <Badge variant="secondary" className="text-[10px] font-medium bg-muted/80 text-muted-foreground border-0 px-2 py-0.5">{model.family}</Badge>;
    $[36] = model.family;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== bestDiscount || $[39] !== t) {
    t17 = bestDiscount > 0 && <Badge className="text-[10px] px-2 py-0.5 font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">{t("public.models.discount.off", {
        percent: Math.round(bestDiscount * 100)
      })}</Badge>;
    $[38] = bestDiscount;
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== allHaveDeactivatedAt || $[42] !== deactivationAllPast) {
    t18 = allHaveDeactivatedAt && <ModelStatusBadge status="deactivated" isPast={deactivationAllPast} />;
    $[41] = allHaveDeactivatedAt;
    $[42] = deactivationAllPast;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== allHaveDeprecatedAt || $[45] !== deprecationAllPast) {
    t19 = allHaveDeprecatedAt && <ModelStatusBadge status="deprecated" isPast={deprecationAllPast} />;
    $[44] = allHaveDeprecatedAt;
    $[45] = deprecationAllPast;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t16 || $[48] !== t17 || $[49] !== t18 || $[50] !== t19) {
    t20 = <div className="flex flex-wrap items-center gap-1.5">{t16}{t17}{t18}{t19}</div>;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t15 || $[53] !== t20) {
    t21 = <div className="mb-4">{t15}{t20}</div>;
    $[52] = t15;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== model.id) {
    t22 = <code className="text-xs font-mono text-muted-foreground flex-1 truncate">{model.id}</code>;
    $[55] = model.id;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== model.id) {
    t23 = e_1 => {
      e_1.stopPropagation();
      copyToClipboard(model.id);
    };
    $[57] = model.id;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t) {
    t24 = t("public.common.copyModelId");
    $[59] = t;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== copiedModel || $[62] !== model.id) {
    t25 = copiedModel === model.id ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />;
    $[61] = copiedModel;
    $[62] = model.id;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t23 || $[65] !== t24 || $[66] !== t25) {
    t26 = <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0 text-muted-foreground/60 hover:text-foreground hover:bg-transparent" onClick={t23} title={t24}>{t25}</Button>;
    $[64] = t23;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t22 || $[69] !== t26) {
    t27 = <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-md bg-muted/50 border border-border/50 mb-4">{t22}{t26}</div>;
    $[68] = t22;
    $[69] = t26;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== capabilities || $[72] !== t) {
    t28 = capabilities.length > 0 && <div className="flex flex-wrap gap-1.5 mb-4">{capabilities.map(t29 => {
        const {
          icon: Icon,
          label,
          color
        } = t29;
        return <Tooltip key={label}><TooltipTrigger asChild={true}><div className={cn("inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium cursor-help transition-colors", "bg-muted/50 text-muted-foreground hover:bg-muted border border-transparent hover:border-border/50")}><Icon size={12} className={color} />{label}</div></TooltipTrigger><TooltipContent side="top" className="text-xs">{t("public.models.card.supports", {
              capability: label.toLowerCase()
            })}</TooltipContent></Tooltip>;
      })}</div>;
    $[71] = capabilities;
    $[72] = t;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  let t29;
  if ($[74] !== groupedByProvider || $[75] !== showAllProviders) {
    t29 = showAllProviders ? groupedByProvider : groupedByProvider.slice(0, 1);
    $[74] = groupedByProvider;
    $[75] = showAllProviders;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] !== copiedModel || $[78] !== formatPrice || $[79] !== model || $[80] !== t29) {
    t30 = t29.map(t31 => {
      const {
        providerInfo: providerInfo_0,
        providerId,
        mappings
      } = t31;
      const ProviderIcon = getProviderIcon(providerId);
      const hasRegions = mappings.length > 1 || mappings.length === 1 && !!mappings[0].region;
      return <ProviderSection key={providerId} modelId={model.id} providerInfo={providerInfo_0} providerId={providerId} ProviderIcon={ProviderIcon} mappings={mappings} hasRegions={hasRegions} hasProviderStabilityWarning={hasProviderStabilityWarning} formatPrice={formatPrice} copyToClipboard={copyToClipboard} copiedModel={copiedModel} isImageGen={isImageGenModel(model)} />;
    });
    $[77] = copiedModel;
    $[78] = formatPrice;
    $[79] = model;
    $[80] = t29;
    $[81] = t30;
  } else {
    t30 = $[81];
  }
  let t31;
  if ($[82] !== groupedByProvider.length || $[83] !== showAllProviders || $[84] !== t) {
    t31 = groupedByProvider.length > 1 && <button type="button" className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors" onClick={e_2 => {
      e_2.stopPropagation();
      setShowAllProviders(_temp6);
    }}>{showAllProviders ? <><ChevronUp className="h-3.5 w-3.5" />{t("public.models.card.fewerProviders")}</> : <><ChevronDown className="h-3.5 w-3.5" />{t(groupedByProvider.length - 1 > 1 ? "public.models.card.moreProviders" : "public.models.card.moreProvider", {
          count: groupedByProvider.length - 1
        })}</>}</button>;
    $[82] = groupedByProvider.length;
    $[83] = showAllProviders;
    $[84] = t;
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  let t32;
  if ($[86] !== t30 || $[87] !== t31) {
    t32 = <div className="space-y-3">{t30}{t31}</div>;
    $[86] = t30;
    $[87] = t31;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  const t33 = `${groupedByProvider[0]?.providerId}/${model.id}`;
  let t34;
  if ($[89] !== model.output || $[90] !== t33) {
    t34 = <div className="mt-4 pt-4 border-t border-border/30"><ModelCtaButton modelId={t33} output={model.output} onClick={_temp7} /></div>;
    $[89] = model.output;
    $[90] = t33;
    $[91] = t34;
  } else {
    t34 = $[91];
  }
  let t35;
  if ($[92] !== t21 || $[93] !== t27 || $[94] !== t28 || $[95] !== t32 || $[96] !== t34) {
    t35 = <div className="p-5">{t21}{t27}{t28}{t32}{t34}</div>;
    $[92] = t21;
    $[93] = t27;
    $[94] = t28;
    $[95] = t32;
    $[96] = t34;
    $[97] = t35;
  } else {
    t35 = $[97];
  }
  let t36;
  if ($[98] !== goToModel || $[99] !== t35) {
    t36 = <TooltipProvider><Card className="group relative overflow-hidden border border-border/50 bg-background hover:border-border transition-all duration-200 cursor-pointer py-0" onClick={goToModel}>{t9}{t35}</Card></TooltipProvider>;
    $[98] = goToModel;
    $[99] = t35;
    $[100] = t36;
  } else {
    t36 = $[100];
  }
  return t36;
}
function _temp7(e_3) {
  return e_3.stopPropagation();
}
function _temp6(v) {
  return !v;
}
function _temp5(e_0) {
  return e_0.stopPropagation();
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp3(provider_3) {
  return provider_3.stability !== null && provider_3.stability !== undefined && ["unstable", "experimental"].includes(provider_3.stability);
}
function _temp2(t0) {
  const {
    provider: provider_0
  } = t0;
  return provider_0.deprecatedAt;
}
function _temp(t0) {
  const {
    provider
  } = t0;
  return provider.deactivatedAt;
}
function ShareDropdown(t0) {
  const $ = _c(19);
  const {
    modelId,
    providerId
  } = t0;
  const {
    t
  } = useI18n();
  const [urlCopied, setUrlCopied] = useState(false);
  const shareUrl = `https://doteb.com/models/${encodeURIComponent(modelId)}/${encodeURIComponent(providerId)}`;
  let t1;
  if ($[0] !== t) {
    t1 = t("public.common.share");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Share2 className="h-3 w-3" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground/60 hover:text-foreground hover:bg-transparent" onClick={_temp8} title={t1}>{t2}</Button></DropdownMenuTrigger>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== shareUrl) {
    t4 = async e_0 => {
      e_0.stopPropagation();
      await navigator.clipboard.writeText(shareUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    };
    $[5] = shareUrl;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== urlCopied) {
    t5 = urlCopied ? <Check className="h-4 w-4 mr-2 text-green-500" /> : <Copy className="h-4 w-4 mr-2" />;
    $[7] = urlCopied;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t || $[10] !== urlCopied) {
    t6 = urlCopied ? t("public.common.copied") : t("public.common.copyUrl");
    $[9] = t;
    $[10] = urlCopied;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <DropdownMenuContent align="end"><DropdownMenuItem onClick={t4} className="cursor-pointer">{t5}{t6}</DropdownMenuItem></DropdownMenuContent>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t7) {
    t8 = <DropdownMenu>{t3}{t7}</DropdownMenu>;
    $[16] = t3;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function _temp8(e) {
  return e.stopPropagation();
}
export function ProviderSection(t0) {
  const $ = _c(161);
  const {
    modelId,
    providerInfo,
    providerId,
    ProviderIcon,
    mappings,
    hasRegions,
    hasProviderStabilityWarning,
    formatPrice,
    copyToClipboard,
    copiedModel,
    isImageGen: t1
  } = t0;
  const isImageGen = t1 === undefined ? false : t1;
  const {
    t
  } = useI18n();
  const [activeRegionIdx, setActiveRegionIdx] = useState(0);
  const [showTokenPricing, setShowTokenPricing] = useState(false);
  const [selectedServiceTierId, setSelectedServiceTierId] = useState("standard");
  const activeMapping = mappings[activeRegionIdx] ?? mappings[0];
  let t2;
  if ($[0] !== activeMapping.serviceTiers) {
    t2 = activeMapping.serviceTiers ?? [];
    $[0] = activeMapping.serviceTiers;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = new Set(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const supportedServiceTierIds = t3;
  let hasImageCostEstimate;
  let providerModelId;
  let serviceTierMultiplier;
  let shouldShowTokenPricing;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[4] !== ProviderIcon || $[5] !== activeMapping || $[6] !== hasProviderStabilityWarning || $[7] !== isImageGen || $[8] !== modelId || $[9] !== providerId || $[10] !== providerInfo.name || $[11] !== providerInfo.serviceTiers || $[12] !== selectedServiceTierId || $[13] !== showTokenPricing || $[14] !== supportedServiceTierIds || $[15] !== t) {
    let t9;
    if ($[25] !== supportedServiceTierIds) {
      t9 = tier => supportedServiceTierIds.has(tier.id);
      $[25] = supportedServiceTierIds;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    const serviceTiers = (providerInfo.serviceTiers ?? []).filter(t9);
    const activeServiceTierId = selectedServiceTierId === "standard" || serviceTiers.some(tier_0 => tier_0.id === selectedServiceTierId) ? selectedServiceTierId : "standard";
    serviceTierMultiplier = serviceTiers.find(tier_1 => tier_1.id === activeServiceTierId)?.multiplier ?? 1;
    providerModelId = activeMapping.region ? `${providerId}/${modelId}:${activeMapping.region}` : `${providerId}/${modelId}`;
    let t10;
    if ($[27] !== activeMapping) {
      t10 = hasEstimatedImageCost(activeMapping);
      $[27] = activeMapping;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    hasImageCostEstimate = t10;
    shouldShowTokenPricing = !isImageGen || showTokenPricing || !hasImageCostEstimate;
    t8 = "flex flex-1 flex-col rounded-lg border border-border/50 bg-muted/20 overflow-hidden";
    t6 = "flex items-center justify-between gap-2 px-3 py-2.5 border-b border-border/30";
    let t11;
    if ($[29] !== ProviderIcon || $[30] !== providerId || $[31] !== providerInfo.name) {
      t11 = <div className="w-5 h-5 rounded flex items-center justify-center shrink-0">{ProviderIcon ? <ProviderIcon className="h-4 w-4" /> : <span className="text-[10px] font-bold text-muted-foreground">{(providerInfo?.name ?? providerId).charAt(0).toUpperCase()}</span>}</div>;
      $[29] = ProviderIcon;
      $[30] = providerId;
      $[31] = providerInfo.name;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    const t12 = providerInfo?.name ?? providerId;
    let t13;
    if ($[33] !== t12) {
      t13 = <span className="text-sm font-semibold text-foreground truncate">{t12}</span>;
      $[33] = t12;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    let t14;
    if ($[35] !== activeMapping.stability) {
      t14 = <StabilityDot stability={activeMapping.stability} />;
      $[35] = activeMapping.stability;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    let t15;
    if ($[37] !== activeMapping || $[38] !== hasProviderStabilityWarning) {
      t15 = hasProviderStabilityWarning(activeMapping) && <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />;
      $[37] = activeMapping;
      $[38] = hasProviderStabilityWarning;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    if ($[40] !== t11 || $[41] !== t13 || $[42] !== t14 || $[43] !== t15) {
      t7 = <div className="flex items-center gap-2 min-w-0">{t11}{t13}{t14}{t15}</div>;
      $[40] = t11;
      $[41] = t13;
      $[42] = t14;
      $[43] = t15;
      $[44] = t7;
    } else {
      t7 = $[44];
    }
    t4 = "flex items-center gap-1 shrink-0";
    t5 = serviceTiers.length > 0 && <div className="flex h-6 items-center rounded-md border border-border/50 bg-background/80 p-0.5" aria-label={t("public.models.card.serviceTier")}><button type="button" onClick={() => setSelectedServiceTierId("standard")} className={cn("inline-flex h-5 items-center gap-1 rounded px-1.5 text-[10px] font-medium leading-none transition-colors", activeServiceTierId === "standard" ? "bg-muted text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")} aria-pressed={activeServiceTierId === "standard"} title={t("public.models.card.standardPricing")}><span>{t("public.models.card.standardShort")}</span><span className={cn("text-[9px] font-medium", activeServiceTierId === "standard" ? "text-foreground/65" : "text-muted-foreground/70")}>1x</span></button>{serviceTiers.map(tier_2 => {
        const isSelected = activeServiceTierId === tier_2.id;
        return <button key={tier_2.id} type="button" onClick={() => setSelectedServiceTierId(tier_2.id)} className={cn("inline-flex h-5 items-center gap-1 rounded px-1.5 text-[10px] font-medium leading-none transition-colors", isSelected ? "bg-muted text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")} aria-pressed={isSelected} title={`${tier_2.name} (${tier_2.multiplier}x pricing)`}><span>{tier_2.name}</span><span className={cn("text-[9px] font-medium", isSelected ? "text-foreground/65" : "text-muted-foreground/70")}>{tier_2.multiplier}x</span></button>;
      })}</div>;
    $[4] = ProviderIcon;
    $[5] = activeMapping;
    $[6] = hasProviderStabilityWarning;
    $[7] = isImageGen;
    $[8] = modelId;
    $[9] = providerId;
    $[10] = providerInfo.name;
    $[11] = providerInfo.serviceTiers;
    $[12] = selectedServiceTierId;
    $[13] = showTokenPricing;
    $[14] = supportedServiceTierIds;
    $[15] = t;
    $[16] = hasImageCostEstimate;
    $[17] = providerModelId;
    $[18] = serviceTierMultiplier;
    $[19] = shouldShowTokenPricing;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    hasImageCostEstimate = $[16];
    providerModelId = $[17];
    serviceTierMultiplier = $[18];
    shouldShowTokenPricing = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
  }
  let t9;
  if ($[45] !== modelId || $[46] !== providerId) {
    t9 = <ShareDropdown modelId={modelId} providerId={providerId} />;
    $[45] = modelId;
    $[46] = providerId;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  let t10;
  if ($[48] !== copyToClipboard || $[49] !== providerModelId) {
    t10 = e => {
      e.stopPropagation();
      copyToClipboard(providerModelId);
    };
    $[48] = copyToClipboard;
    $[49] = providerModelId;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  let t11;
  if ($[51] !== t) {
    t11 = t("public.models.card.copyProviderModelId");
    $[51] = t;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== copiedModel || $[54] !== providerModelId) {
    t12 = copiedModel === providerModelId ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />;
    $[53] = copiedModel;
    $[54] = providerModelId;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  let t13;
  if ($[56] !== t10 || $[57] !== t11 || $[58] !== t12) {
    t13 = <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground/60 hover:text-foreground hover:bg-transparent" onClick={t10} title={t11}>{t12}</Button>;
    $[56] = t10;
    $[57] = t11;
    $[58] = t12;
    $[59] = t13;
  } else {
    t13 = $[59];
  }
  let t14;
  if ($[60] !== providerModelId) {
    t14 = <div onClick={_temp9} onMouseDown={_temp10}><ModelCodeExampleDialog modelId={providerModelId} /></div>;
    $[60] = providerModelId;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  let t15;
  if ($[62] !== t13 || $[63] !== t14 || $[64] !== t4 || $[65] !== t5 || $[66] !== t9) {
    t15 = <div className={t4}>{t5}{t9}{t13}{t14}</div>;
    $[62] = t13;
    $[63] = t14;
    $[64] = t4;
    $[65] = t5;
    $[66] = t9;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  let t16;
  if ($[68] !== t15 || $[69] !== t6 || $[70] !== t7) {
    t16 = <div className={t6}>{t7}{t15}</div>;
    $[68] = t15;
    $[69] = t6;
    $[70] = t7;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  let t17;
  if ($[72] !== activeRegionIdx || $[73] !== hasRegions || $[74] !== mappings || $[75] !== t) {
    t17 = hasRegions && mappings.length > 1 && <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-border/30 bg-muted/30 overflow-x-auto"><Globe className="h-3 w-3 text-muted-foreground/50 shrink-0 mr-1" />{mappings.map((mapping, idx) => <button key={`${mapping.providerId}-${mapping.region ?? "default"}-${idx}`} type="button" onClick={e_2 => {
        e_2.stopPropagation();
        setActiveRegionIdx(idx);
      }} className={cn("px-2 py-1 rounded text-[10px] font-medium transition-colors whitespace-nowrap", activeRegionIdx === idx ? "bg-background text-foreground shadow-sm border border-border/50" : "text-muted-foreground hover:text-foreground")}>{mapping.region ?? t("public.common.default")}</button>)}</div>;
    $[72] = activeRegionIdx;
    $[73] = hasRegions;
    $[74] = mappings;
    $[75] = t;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[77] !== t) {
    t18 = t("public.models.card.context");
    $[77] = t;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== activeMapping.contextSize) {
    t19 = activeMapping.contextSize ? formatContextSize(activeMapping.contextSize) : "\u2014";
    $[79] = activeMapping.contextSize;
    $[80] = t19;
  } else {
    t19 = $[80];
  }
  let t20;
  if ($[81] !== t19) {
    t20 = <span className="text-foreground font-medium">{t19}</span>;
    $[81] = t19;
    $[82] = t20;
  } else {
    t20 = $[82];
  }
  let t21;
  if ($[83] !== t18 || $[84] !== t20) {
    t21 = <span className="text-muted-foreground">{t18}{" "}{t20}</span>;
    $[83] = t18;
    $[84] = t20;
    $[85] = t21;
  } else {
    t21 = $[85];
  }
  let t22;
  if ($[86] !== activeMapping.discount || $[87] !== t) {
    t22 = activeMapping.discount && parseFloat(activeMapping.discount) > 0 && <Badge className="text-[10px] px-1.5 py-0 h-4 font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">{t("public.models.discount.off", {
        percent: Math.round(parseFloat(activeMapping.discount) * 100)
      })}</Badge>;
    $[86] = activeMapping.discount;
    $[87] = t;
    $[88] = t22;
  } else {
    t22 = $[88];
  }
  let t23;
  if ($[89] !== t21 || $[90] !== t22) {
    t23 = <div className="flex items-center justify-between text-xs">{t21}{t22}</div>;
    $[89] = t21;
    $[90] = t22;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  let t24;
  if ($[92] !== activeMapping.deactivatedAt || $[93] !== activeMapping.deprecatedAt) {
    t24 = (activeMapping.deprecatedAt ?? activeMapping.deactivatedAt) && <div className="flex flex-wrap gap-1.5">{activeMapping.deprecatedAt && <Badge variant="outline" className="text-[10px] px-2 py-0.5 gap-1 bg-amber-500/5 text-amber-600 dark:text-amber-400 border-amber-500/20"><AlertTriangle className="h-2.5 w-2.5" />{formatDeprecationDate(activeMapping.deprecatedAt, "deprecated")}</Badge>}{activeMapping.deactivatedAt && <Badge variant="outline" className="text-[10px] px-2 py-0.5 gap-1 bg-red-500/5 text-red-600 dark:text-red-400 border-red-500/20"><AlertCircle className="h-2.5 w-2.5" />{formatDeprecationDate(activeMapping.deactivatedAt, "deactivated")}</Badge>}</div>;
    $[92] = activeMapping.deactivatedAt;
    $[93] = activeMapping.deprecatedAt;
    $[94] = t24;
  } else {
    t24 = $[94];
  }
  let t25;
  if ($[95] !== activeMapping.discount || $[96] !== activeMapping.imageOutputPrice || $[97] !== activeMapping.imageOutputTokensByResolution || $[98] !== activeMapping.requestPrice || $[99] !== isImageGen || $[100] !== serviceTierMultiplier || $[101] !== t) {
    t25 = isImageGen && (() => {
      const discountNum = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
      const requestPriceNum = activeMapping.requestPrice !== null && activeMapping.requestPrice !== undefined ? parseFloat(activeMapping.requestPrice) : 0;
      let perImage = null;
      let label = t("public.models.card.perImage");
      let outputCost = 0;
      let resolutionKey = null;
      if (activeMapping.imageOutputPrice && activeMapping.imageOutputTokensByResolution) {
        const outPrice = parseFloat(activeMapping.imageOutputPrice);
        const entries = Object.entries(activeMapping.imageOutputTokensByResolution);
        const preferred = entries.find(_temp11) ?? entries[0];
        if (preferred) {
          outputCost = preferred[1] * outPrice;
          resolutionKey = preferred[0];
        }
        if (requestPriceNum > 0 || outputCost > 0) {
          perImage = (requestPriceNum + outputCost) * serviceTierMultiplier;
          if (resolutionKey) {
            label = t("public.models.card.perImageWithResolution", {
              resolution: resolutionKey
            });
          }
        }
      }
      if (perImage === null) {
        return null;
      }
      const discounted = perImage * (1 - discountNum);
      return <div className="rounded-md bg-muted/40 border border-border/30 p-3"><div className="flex items-center justify-between gap-2"><div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{label}</div><div className="font-semibold tabular-nums text-sm">{discountNum > 0 ? <><span className="line-through text-muted-foreground mr-1 text-xs">${perImage.toFixed(4)}</span><span className="text-green-600">${discounted.toFixed(4)}</span></> : `$${perImage.toFixed(4)}`}</div></div></div>;
    })();
    $[95] = activeMapping.discount;
    $[96] = activeMapping.imageOutputPrice;
    $[97] = activeMapping.imageOutputTokensByResolution;
    $[98] = activeMapping.requestPrice;
    $[99] = isImageGen;
    $[100] = serviceTierMultiplier;
    $[101] = t;
    $[102] = t25;
  } else {
    t25 = $[102];
  }
  let t26;
  if ($[103] !== activeMapping.discount || $[104] !== activeMapping.inputCharacterPrice || $[105] !== serviceTierMultiplier || $[106] !== shouldShowTokenPricing || $[107] !== t) {
    t26 = shouldShowTokenPricing && activeMapping.inputCharacterPrice && parseFloat(activeMapping.inputCharacterPrice) > 0 && (() => {
      const discountNum_0 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
      const perThousandChars = parseFloat(activeMapping.inputCharacterPrice) * 1000 * serviceTierMultiplier;
      const formatChars = _temp12;
      return <div className="rounded-md bg-muted/40 border border-border/30 p-2.5"><div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">{t("public.models.card.perCharacterPricing")}</div><div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("public.models.card.inputText")}</span><span className="font-semibold tabular-nums">{discountNum_0 > 0 ? <><span className="line-through text-muted-foreground mr-1 text-xs">{formatChars(perThousandChars)}</span><span className="text-green-600">{formatChars(perThousandChars * (1 - discountNum_0))}</span></> : formatChars(perThousandChars)}<span className="text-muted-foreground/60 text-xs ml-0.5">{t("public.models.card.perThousandChars")}</span></span></div></div>;
    })();
    $[103] = activeMapping.discount;
    $[104] = activeMapping.inputCharacterPrice;
    $[105] = serviceTierMultiplier;
    $[106] = shouldShowTokenPricing;
    $[107] = t;
    $[108] = t26;
  } else {
    t26 = $[108];
  }
  let t27;
  if ($[109] !== activeMapping.cachedInputPrice || $[110] !== activeMapping.discount || $[111] !== activeMapping.inputCharacterPrice || $[112] !== activeMapping.inputPrice || $[113] !== activeMapping.outputPrice || $[114] !== activeMapping.perSecondPrice || $[115] !== formatPrice || $[116] !== serviceTierMultiplier || $[117] !== shouldShowTokenPricing || $[118] !== t) {
    t27 = !shouldShowTokenPricing || activeMapping.inputCharacterPrice && parseFloat(activeMapping.inputCharacterPrice) > 0 ? null : activeMapping.perSecondPrice && Object.keys(activeMapping.perSecondPrice).length > 0 ? <div className="rounded-md bg-muted/40 border border-border/30 p-2.5"><div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">{t("public.models.card.perSecondPricing")}</div><div className="space-y-1">{(() => {
          const prices = activeMapping.perSecondPrice;
          const defaultVideo = prices.default_video;
          const defaultAudio = prices.default_audio;
          if (defaultVideo && defaultAudio) {
            return <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("public.models.card.videoAudio")}</span><span className="font-semibold tabular-nums">${defaultVideo} – ${defaultAudio}<span className="text-muted-foreground/60 text-xs ml-0.5">{t("public.models.card.perSecond")}</span></span></div>;
          }
          const defaultPrice = prices["default"];
          if (defaultPrice) {
            return <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("public.common.default")}</span><span className="font-semibold tabular-nums">${defaultPrice}<span className="text-muted-foreground/60 text-xs ml-0.5">{t("public.models.card.perSecond")}</span></span></div>;
          }
          return Object.entries(prices).map(t28 => {
            const [key, value_0] = t28;
            return <div key={key} className="flex justify-between text-xs"><span className="text-muted-foreground">{key}</span><span className="font-mono tabular-nums">${value_0}{t("public.models.card.perSecond")}</span></div>;
          });
        })()}</div></div> : <div className="space-y-2"><div className="grid grid-cols-3 gap-px rounded-md bg-border/30 border border-border/30 overflow-hidden"><div className="bg-background p-2"><PriceCell label={t("public.common.input")} price={activeMapping.inputPrice} discount={activeMapping.discount} unit={t("public.models.card.perMillionTokens")} formatPrice={formatPrice} multiplier={serviceTierMultiplier} /></div><div className="bg-background p-2"><PriceCell label={t("public.capabilities.cached")} price={activeMapping.cachedInputPrice} discount={activeMapping.discount} unit={t("public.models.card.perMillionTokens")} formatPrice={formatPrice} multiplier={serviceTierMultiplier} /></div><div className="bg-background p-2"><PriceCell label={t("public.common.output")} price={activeMapping.outputPrice} discount={activeMapping.discount} unit={t("public.models.card.perMillionTokens")} formatPrice={formatPrice} multiplier={serviceTierMultiplier} /></div></div></div>;
    $[109] = activeMapping.cachedInputPrice;
    $[110] = activeMapping.discount;
    $[111] = activeMapping.inputCharacterPrice;
    $[112] = activeMapping.inputPrice;
    $[113] = activeMapping.outputPrice;
    $[114] = activeMapping.perSecondPrice;
    $[115] = formatPrice;
    $[116] = serviceTierMultiplier;
    $[117] = shouldShowTokenPricing;
    $[118] = t;
    $[119] = t27;
  } else {
    t27 = $[119];
  }
  let t28;
  if ($[120] !== activeMapping.discount || $[121] !== activeMapping.pricingTiers || $[122] !== formatPrice || $[123] !== serviceTierMultiplier || $[124] !== t) {
    t28 = (activeMapping.pricingTiers?.length ?? 0) > 1 && <div className="rounded-md bg-muted/40 border border-border/30 p-2.5"><div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">{t("public.models.card.tieredPricing")}</div><div className="space-y-2">{(() => {
          const hasCached = activeMapping.pricingTiers.some(_temp13);
          return <><div className={`grid ${hasCached ? "grid-cols-[1fr_1fr_1fr_1fr]" : "grid-cols-[1fr_1fr_1fr]"} gap-x-2 text-[10px] text-muted-foreground/60 text-right`}><div /><div>{t("public.common.inputShort")}</div>{hasCached && <div>{t("public.capabilities.cached")}</div>}<div>{t("public.common.outputShort")}</div></div>{activeMapping.pricingTiers.map((tier_3, index) => {
              const discountNum_1 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
              const prevTokens = activeMapping.pricingTiers[index - 1]?.upToTokens ?? 0;
              const label_0 = tier_3.upToTokens === null ? t("public.models.card.tokenThresholdAbove", {
                count: (prevTokens / 1000).toLocaleString()
              }) : t("public.models.card.tokenThresholdUpTo", {
                count: (tier_3.upToTokens / 1000).toLocaleString()
              });
              return <div key={index} className={`grid ${hasCached ? "grid-cols-[1fr_1fr_1fr_1fr]" : "grid-cols-[1fr_1fr_1fr]"} gap-x-2 items-center text-xs`}><span className="text-muted-foreground">{label_0}</span><div className="font-mono tabular-nums flex justify-end">{formatPrice(tier_3.inputPrice, discountNum_1 > 0 ? String(discountNum_1) : null, "end", serviceTierMultiplier)}</div>{hasCached && <div className="font-mono tabular-nums flex justify-end">{tier_3.cachedInputPrice ? formatPrice(tier_3.cachedInputPrice, discountNum_1 > 0 ? String(discountNum_1) : null, "end", serviceTierMultiplier) : "\u2014"}</div>}<div className="font-mono tabular-nums flex justify-end">{formatPrice(tier_3.outputPrice, discountNum_1 > 0 ? String(discountNum_1) : null, "end", serviceTierMultiplier)}</div></div>;
            })}</>;
        })()}</div></div>;
    $[120] = activeMapping.discount;
    $[121] = activeMapping.pricingTiers;
    $[122] = formatPrice;
    $[123] = serviceTierMultiplier;
    $[124] = t;
    $[125] = t28;
  } else {
    t28 = $[125];
  }
  let t29;
  if ($[126] !== activeMapping.discount || $[127] !== activeMapping.imageInputPrice || $[128] !== activeMapping.imageInputTokensByResolution || $[129] !== activeMapping.imageOutputPrice || $[130] !== activeMapping.imageOutputTokensByResolution || $[131] !== formatPrice || $[132] !== serviceTierMultiplier || $[133] !== t) {
    t29 = (activeMapping.imageInputTokensByResolution ?? activeMapping.imageOutputTokensByResolution ?? activeMapping.imageOutputPrice) && <div className="rounded-md bg-muted/40 border border-border/30 p-2.5"><div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">{activeMapping.imageOutputTokensByResolution ? t("public.models.card.imagePricingEstimated") : t("public.models.card.imagePricing")}</div>{activeMapping.imageInputPrice && activeMapping.imageInputTokensByResolution && (() => {
        const imageInputPriceNum = parseFloat(activeMapping.imageInputPrice);
        const named = Object.entries(activeMapping.imageInputTokensByResolution).filter(_temp14);
        const defaultTokens = activeMapping.imageInputTokensByResolution["default"];
        const entries_0 = named.length > 0 ? named : defaultTokens !== undefined ? [[t("public.models.card.anySize"), defaultTokens]] : [];
        if (entries_0.length === 0) {
          return null;
        }
        const discountNum_2 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
        return <div className="mb-1.5"><div className="text-[10px] text-muted-foreground mb-0.5">{t("public.common.input")}</div>{entries_0.map(t30 => {
            const [res, tokensPerImage] = t30;
            const raw = tokensPerImage * imageInputPriceNum * serviceTierMultiplier;
            const discounted_0 = raw * (1 - discountNum_2);
            return <div key={res} className="flex justify-between items-center text-xs py-0.5"><span className="text-muted-foreground">{res}</span><span className="font-mono tabular-nums">{discountNum_2 > 0 ? <><span className="line-through text-muted-foreground mr-1">~${raw.toFixed(4)}</span><span className="text-green-600 font-semibold">~${discounted_0.toFixed(4)}</span></> : `~$${raw.toFixed(4)}`}</span></div>;
          })}</div>;
      })()}{activeMapping.imageOutputPrice && !activeMapping.imageOutputTokensByResolution && <div><div className="text-[10px] text-muted-foreground mb-0.5">{t("public.models.card.imageOutput")}</div><div className="flex justify-between items-center text-xs py-0.5"><span className="text-muted-foreground">{t("public.common.tokens")}</span><div className="font-mono tabular-nums flex items-center gap-1">{formatPrice(activeMapping.imageOutputPrice, activeMapping.discount, "end", serviceTierMultiplier)}<span className="text-muted-foreground/60">{t("public.models.card.perMillionTokens")}</span></div></div></div>}{activeMapping.imageOutputPrice && activeMapping.imageOutputTokensByResolution && (() => {
        const imageOutputPriceNum = parseFloat(activeMapping.imageOutputPrice);
        const entries_1 = Object.entries(activeMapping.imageOutputTokensByResolution).filter(_temp15);
        if (entries_1.length === 0) {
          return null;
        }
        const discountNum_3 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
        return <div><div className="text-[10px] text-muted-foreground mb-0.5">{t("public.common.output")}</div>{entries_1.map(t31 => {
            const [res_0, tokensPerImage_0] = t31;
            const raw_0 = tokensPerImage_0 * imageOutputPriceNum * serviceTierMultiplier;
            const discounted_1 = raw_0 * (1 - discountNum_3);
            return <div key={res_0} className="flex justify-between items-center text-xs py-0.5"><span className="text-muted-foreground">{res_0}</span><span className="font-mono tabular-nums">{discountNum_3 > 0 ? <><span className="line-through text-muted-foreground mr-1">~${raw_0.toFixed(4)}</span><span className="text-green-600 font-semibold">~${discounted_1.toFixed(4)}</span></> : `~$${raw_0.toFixed(4)}`}</span></div>;
          })}</div>;
      })()}</div>;
    $[126] = activeMapping.discount;
    $[127] = activeMapping.imageInputPrice;
    $[128] = activeMapping.imageInputTokensByResolution;
    $[129] = activeMapping.imageOutputPrice;
    $[130] = activeMapping.imageOutputTokensByResolution;
    $[131] = formatPrice;
    $[132] = serviceTierMultiplier;
    $[133] = t;
    $[134] = t29;
  } else {
    t29 = $[134];
  }
  let t30;
  if ($[135] !== activeMapping.discount || $[136] !== activeMapping.requestPrice || $[137] !== activeMapping.webSearchPrice || $[138] !== isImageGen || $[139] !== t) {
    t30 = !isImageGen && activeMapping.requestPrice !== null && activeMapping.requestPrice !== undefined && parseFloat(activeMapping.requestPrice) > 0 || activeMapping.webSearchPrice !== null && activeMapping.webSearchPrice !== undefined && parseFloat(activeMapping.webSearchPrice) > 0 ? <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">{!isImageGen && activeMapping.requestPrice !== null && activeMapping.requestPrice !== undefined && parseFloat(activeMapping.requestPrice) > 0 && (() => {
        const original = parseFloat(activeMapping.requestPrice);
        const discountNum_4 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
        return <span>+{" "}{discountNum_4 > 0 ? <><span className="line-through text-muted-foreground/60 mr-1">${original.toFixed(3)}</span><span className="text-green-600 font-semibold">${(original * (1 - discountNum_4)).toFixed(3)}</span></> : `$${original.toFixed(3)}`}<span className="text-muted-foreground/60">{" "}{t("public.models.card.perRequest")}</span></span>;
      })()}{activeMapping.webSearchPrice !== null && activeMapping.webSearchPrice !== undefined && parseFloat(activeMapping.webSearchPrice) > 0 && (() => {
        const original_0 = parseFloat(activeMapping.webSearchPrice);
        const discountNum_5 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
        return <span>+{" "}{discountNum_5 > 0 ? <><span className="line-through text-muted-foreground/60 mr-1">${original_0.toFixed(3)}</span><span className="text-green-600 font-semibold">${(original_0 * (1 - discountNum_5)).toFixed(3)}</span></> : `$${original_0.toFixed(3)}`}<span className="text-muted-foreground/60">{" "}{t("public.models.card.perSearch")}</span></span>;
      })()}</div> : null;
    $[135] = activeMapping.discount;
    $[136] = activeMapping.requestPrice;
    $[137] = activeMapping.webSearchPrice;
    $[138] = isImageGen;
    $[139] = t;
    $[140] = t30;
  } else {
    t30 = $[140];
  }
  let t31;
  if ($[141] !== hasImageCostEstimate || $[142] !== isImageGen || $[143] !== showTokenPricing || $[144] !== t) {
    t31 = isImageGen && hasImageCostEstimate && <button type="button" className="w-full flex items-center justify-center gap-1.5 py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors" onClick={e_3 => {
      e_3.stopPropagation();
      setShowTokenPricing(_temp16);
    }}>{showTokenPricing ? <><ChevronUp className="h-3 w-3" />{t("public.models.card.hideTokenPricing")}</> : <><ChevronDown className="h-3 w-3" />{t("public.models.card.expandDetails")}</>}</button>;
    $[141] = hasImageCostEstimate;
    $[142] = isImageGen;
    $[143] = showTokenPricing;
    $[144] = t;
    $[145] = t31;
  } else {
    t31 = $[145];
  }
  let t32;
  if ($[146] !== t23 || $[147] !== t24 || $[148] !== t25 || $[149] !== t26 || $[150] !== t27 || $[151] !== t28 || $[152] !== t29 || $[153] !== t30 || $[154] !== t31) {
    t32 = <div className="px-3 py-3 space-y-3">{t23}{t24}{t25}{t26}{t27}{t28}{t29}{t30}{t31}</div>;
    $[146] = t23;
    $[147] = t24;
    $[148] = t25;
    $[149] = t26;
    $[150] = t27;
    $[151] = t28;
    $[152] = t29;
    $[153] = t30;
    $[154] = t31;
    $[155] = t32;
  } else {
    t32 = $[155];
  }
  let t33;
  if ($[156] !== t16 || $[157] !== t17 || $[158] !== t32 || $[159] !== t8) {
    t33 = <div className={t8}>{t16}{t17}{t32}</div>;
    $[156] = t16;
    $[157] = t17;
    $[158] = t32;
    $[159] = t8;
    $[160] = t33;
  } else {
    t33 = $[160];
  }
  return t33;
}
function _temp16(v) {
  return !v;
}
function _temp15(t0) {
  const [k_1] = t0;
  return k_1 !== "default";
}
function _temp14(t0) {
  const [k_0] = t0;
  return k_0 !== "default";
}
function _temp13(t_0) {
  return t_0.cachedInputPrice;
}
function _temp12(value) {
  return `$${parseFloat(value.toFixed(4))}`;
}
function _temp11(t0) {
  const [k] = t0;
  return k !== "default";
}
function _temp10(e_1) {
  return e_1.stopPropagation();
}
function _temp9(e_0) {
  return e_0.stopPropagation();
}